import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import useSWR from 'swr';
import { Plus, Eye, Pencil, ArrowUpDown, ArrowUp, ArrowDown, Search, MessageSquare } from 'lucide-react';
import { getAccounts } from '@/services/api';
import { Account } from '@/types/account';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface AccountListProps {
  onView?: (account: Account) => void;
  onEdit?: (account: Account) => void;
  onCreate?: () => void;
}

type SortColumn = 'accountToken' | 'name' | 'productName' | 'remark' | 'isActive' | 'createdTimestamp' | 'createdBy';
type SortDirection = 'asc' | 'desc';

export const AccountList: React.FC<AccountListProps> = ({
  onView,
  onEdit,
  onCreate,
}) => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [sortColumn, setSortColumn] = useState<SortColumn>('createdTimestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const currentUserEmail = instance.getActiveAccount()?.username;

  const { data: accounts, error, isLoading, mutate } = useSWR(
    'accounts',
    () => getAccounts(instance)
  );

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedAccounts = useMemo(() => {
    if (!accounts) return [];
    if (!Array.isArray(accounts)) {
      console.error('Accounts is not an array:', accounts);
      return [];
    }

    let filtered = showOnlyMine && currentUserEmail
      ? accounts.filter(account => account.createdBy === currentUserEmail)
      : accounts;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(account =>
        account.name.toLowerCase().includes(query) ||
        account.accountToken.toLowerCase().includes(query) ||
        (account.productName?.toLowerCase().includes(query)) ||
        (account.remark?.toLowerCase().includes(query))
      );
    }

    const sorted = [...filtered].sort((a, b) => {
      let aValue: string | boolean | Date | null;
      let bValue: string | boolean | Date | null;

      switch (sortColumn) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'createdTimestamp':
          aValue = new Date(a.createdTimestamp);
          bValue = new Date(b.createdTimestamp);
          break;
        case 'productName':
          aValue = a.productName || '';
          bValue = b.productName || '';
          break;
        default:
          aValue = a[sortColumn];
          bValue = b[sortColumn];
      }

      if (aValue === null || aValue === '') return sortDirection === 'asc' ? 1 : -1;
      if (bValue === null || bValue === '') return sortDirection === 'asc' ? -1 : 1;

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [accounts, sortColumn, sortDirection, showOnlyMine, currentUserEmail, searchQuery]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="ml-1 h-3 w-3" />
      : <ArrowDown className="ml-1 h-3 w-3" />;
  };

  const SortableHeader = ({ column, children }: { column: SortColumn; children: React.ReactNode }) => (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 data-[state=open]:bg-accent"
      onClick={() => handleSort(column)}
    >
      {children}
      <SortIcon column={column} />
    </Button>
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-9 w-28" />
        </div>
        <div className="border rounded-md">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load accounts. Please try again.
          <Button variant="link" className="ml-2 p-0 h-auto" onClick={() => mutate()}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Empty state
  if (!accounts || accounts.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">No accounts found.</p>
        {onCreate && (
          <Button onClick={onCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Create First Account
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {onCreate && (
        <div className="flex items-center justify-end">
          <Button onClick={onCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Create New
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search accounts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="show-only-mine"
            checked={showOnlyMine}
            onCheckedChange={setShowOnlyMine}
          />
          <Label htmlFor="show-only-mine" className="text-sm cursor-pointer">
            My accounts
          </Label>
        </div>
      </div>

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <SortableHeader column="accountToken">Token</SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader column="name">Name</SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader column="productName">Product</SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader column="remark">Remark</SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader column="isActive">Status</SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader column="createdTimestamp">Created</SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader column="createdBy">Created By</SortableHeader>
              </TableHead>
              <TableHead className="w-[130px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedAccounts.map((account) => (
              <TableRow key={account.accountToken}>
                <TableCell className="font-mono text-xs max-w-[180px] truncate cx-mask" title={account.accountToken}>
                  {account.accountToken}
                </TableCell>
                <TableCell className="font-medium">{account.name}</TableCell>
                <TableCell>{account.productName || '-'}</TableCell>
                <TableCell className="max-w-[150px] truncate" title={account.remark || ''}>
                  {account.remark || '-'}
                </TableCell>
                <TableCell>
                  <Badge variant={account.isActive ? 'success' : 'secondary'}>
                    {account.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(account.createdTimestamp)}
                </TableCell>
                <TableCell className="max-w-[150px] truncate" title={account.createdBy}>
                  {account.createdBy}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      title="View Feedback"
                      onClick={() => navigate(`/feedback?account=${account.accountToken}`)}
                    >
                      <MessageSquare className="h-4 w-4" />
                    </Button>
                    {onView && (
                      <Button
                        variant="ghost"
                        size="sm"
                        title="View Details"
                        onClick={() => onView(account)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    {onEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        title="Edit"
                        onClick={() => onEdit(account)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
